import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  AutoComplete,
  Button,
  Space,
  Tabs,
  Card,
  Typography,
  Alert,
  Divider,
  Row,
  Col,
  Switch,
  message,
} from 'antd';
import {
  InfoCircleOutlined,
  FileTextOutlined,
  HistoryOutlined,
  SaveOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import { templateService, TemplateFile, CreateTemplateRequest } from '../services/templateService';
import { companyService, Company } from '../services/companyService';
import TemplateVisualEditor from './TemplateVisualEditor';
import TemplateVersionHistory from './TemplateVersionHistory';
import { useI18n } from '../hooks/useI18n';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface TemplateEditorModalProps {
  visible: boolean;
  template: TemplateFile | null;
  onCancel: () => void;
  onSave: (templateData: CreateTemplateRequest) => void;
}

const TemplateEditorModal: React.FC<TemplateEditorModalProps> = ({
  visible,
  template,
  onCancel,
  onSave,
}) => {
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('editor');
  const [templateContent, setTemplateContent] = useState<any>(null);
  const [plainEditContent, setPlainEditContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const { t } = useI18n();

  useEffect(() => {
    if (visible) {
      loadCompanies();
    }
  }, [visible]);

  const loadCompanies = async () => {
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await companyService.getCompanies(currentTenant);
      if (response.success) {
        setCompanies(response.data || []);
      }
    } catch (error) {
      console.error('加载公司列表失败:', error);
    }
  };

  useEffect(() => {
    if (visible) {
      if (template) {
        // 编辑模式 - 从数据库获取完整数据
        loadTemplateData(template.id);
      } else {
        // 创建模式
        form.resetFields();
        setTemplateContent(null);
        // 为新模板提供基本的 JSON Schema 模板
        setPlainEditContent(`{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "type": "object",
  "properties": {},
  "required": []
}`);
        setActiveTab('editor');
      }
    }
  }, [visible, template, form]);

  const loadTemplateData = async (templateId: number) => {
    try {
      setLoading(true);
      // 获取基本信息
      const templateData = await templateService.getTemplateById(templateId);
      
      // 获取完整属性结构
      const fullTemplate = await templateService.getTemplateWithProperties(templateData.template_code);
      
      // 设置表单数据（以 getTemplateById 返回为准，保持与基础信息一致）
      const base: any = templateData || {};
      form.setFieldsValue({
        template_name: base.template_name || base.schema_name || base.name || '',
        template_code: base.template_code || base.schema_code || base.code || '',
        display_name: base.display_name || base.template_name || base.schema_name || '',
        description: base.description || '',
        version: base.version || '1.0.0',
        is_active: !!base.is_active,
        company_id: base.company_id,
        business_type: base.business_type,
        customer_id: base.customer_id,
      });
      
      // 设置模板内容
      setTemplateContent(fullTemplate);
      
      // 加载 JSON 格式用于 Plain Edit
      try {
        const jsonTemplate = await templateService.convertToJsonTemplate(templateId);
        setPlainEditContent(JSON.stringify(jsonTemplate, null, 2));
      } catch (error) {
        console.error('加载 JSON 模板失败:', error);
        setPlainEditContent('');
      }
      
      setActiveTab('editor');
    } catch (error: any) {
      console.error('加载模板数据失败:', error);
      message.error(t('templates.editor.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      let finalContent: any = null;
      
      // 如果当前在 Plain Edit 标签页，使用 JSON 内容
      if (activeTab === 'plain') {
        try {
          // 解析 JSON 字符串
          const parsedContent = JSON.parse(plainEditContent);
          finalContent = parsedContent;
        } catch (error) {
          message.error(t('templates.editor.plainEdit.jsonParseError') || 'JSON 格式错误，请检查内容');
          return;
        }
      } else {
        // 否则使用 Visual Editor 的内容
        // 将 DB 树状属性结构转换为 schema properties 结构（仅当仍为数组时）
        const normalizeTemplateContent = (content: any) => {
          if (!content) return content;
          // 若已是对象映射，直接返回
          if (content.properties && !Array.isArray(content.properties)) return content;

          const buildFromDbNodes = (nodes: any[]): { props: any; req: string[] } => {
            const props: any = {};
            const req: string[] = [];
            (nodes || []).forEach((n: any) => {
              const name = n.property_name || n.display_name || n.name;
              const type = n.property_type || n.type || 'string';
              const desc = n.description || '';
              const itemType = n.item_type || n?.array_items?.items_type;
              const children = Array.isArray(n.children) ? n.children : [];

              const def: any = { type, description: desc };

              if (type === 'array') {
                def.items = {
                  type: itemType || 'string',
                  description: n?.array_items?.items_description || ''
                };
                if ((itemType === 'object') && children.length > 0) {
                  const inner = buildFromDbNodes(children);
                  def.items.properties = inner.props;
                  if (inner.req.length > 0) def.items.required = inner.req;
                }
              } else if (type === 'object') {
                if (children.length > 0) {
                  const inner = buildFromDbNodes(children);
                  def.properties = inner.props;
                  if (inner.req.length > 0) def.required = inner.req;
                }
              }

              props[name] = def;
              if (n.is_required) req.push(name);
            });
            return { props, req };
          };

          const tree = Array.isArray(content.properties) ? content.properties : [];
          const built = buildFromDbNodes(tree);
          return {
            ...content,
            properties: built.props,
            required: built.req
          };
        };

        finalContent = normalizeTemplateContent(templateContent);
      }

      const templateData: CreateTemplateRequest = {
        template_name: values.template_name,
        template_code: values.template_code,
        display_name: values.display_name,
        description: values.description,
        version: values.version || '1.0.0',
        is_active: values.is_active ?? true,
        template_content: finalContent,
        created_by: 'admin', // 可以从用户信息获取
        company_id: values.company_id,
        business_type: values.business_type,
        customer_id: values.customer_id,
      };

      await onSave(templateData);
    } catch (error) {
      console.error('保存Template失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateContentChange = (content: any) => {
    setTemplateContent(content);
  };

  // 当切换到 Plain Edit 标签页时，如果内容为空且是编辑模式，加载 JSON
  const handleTabChange = async (key: string) => {
    setActiveTab(key);
    
    if (key === 'plain' && template && !plainEditContent) {
      try {
        const jsonTemplate = await templateService.convertToJsonTemplate(template.id);
        setPlainEditContent(JSON.stringify(jsonTemplate, null, 2));
      } catch (error) {
        console.error('加载 JSON 模板失败:', error);
        message.error(t('templates.editor.plainEdit.loadFailed') || '加载 JSON 内容失败');
      }
    }
  };

  const renderBasicForm = () => (
    <Form form={form} layout="vertical">
      <Alert
        message={t('templates.editor.basic.alertTitle')}
        description={t('templates.editor.basic.alertDesc')}
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="template_name"
            label={t('templates.editor.basic.templateName')}
            rules={[{ required: true, message: t('templates.editor.basic.enterTemplateName') }]}
          >
            <Input placeholder={t('templates.editor.basic.enterTemplateName')} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="template_code"
            label={t('templates.editor.basic.templateCode')}
            rules={[
              { required: true, message: t('templates.editor.basic.enterTemplateCode') },
              { pattern: /^[a-z0-9_]+$/, message: t('templates.editor.basic.templateCodePattern') }
            ]}
          >
            <Input placeholder={t('templates.editor.basic.enterTemplateCode')} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="display_name"
        label={t('templates.editor.basic.displayName')}
        rules={[{ required: true, message: t('templates.editor.basic.enterDisplayName') }]}
      >
        <Input placeholder={t('templates.editor.basic.enterDisplayName')} />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="version"
            label={t('templates.editor.basic.version')}
            rules={[{ required: true, message: t('templates.editor.basic.enterVersion') }]}
          >
            <Input placeholder="1.0.0" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="is_active"
            label={t('templates.editor.basic.status')}
            valuePropName="checked"
          >
            <Switch checkedChildren={t('common.active')} unCheckedChildren={t('common.inactive')} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name="company_id"
            label="关联公司"
          >
            <Select
              placeholder="请选择公司"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {companies.map(company => (
                <Select.Option key={company.id} value={company.id}>
                  {company.company_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="business_type"
            label="业务类型"
          >
            <AutoComplete
              placeholder="请选择或输入业务类型"
              allowClear
              options={[
                { value: 'CPO' },
                { value: 'SQ' },
                { value: 'SO' },
                { value: 'APInvoice' },
                { value: 'ARInvoice' },
                { value: 'PO' },
                { value: 'GRPO' },
                { value: 'DN' },
              ]}
              filterOption={(inputValue, option) =>
                option?.value?.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
              }
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="customer_id"
            label="客户"
          >
            <Input
              placeholder="请输入客户ID"
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="description"
        label={t('templates.editor.basic.description')}
      >
        <TextArea
          rows={4}
          placeholder={t('templates.editor.basic.enterDescription')}
        />
      </Form.Item>
    </Form>
  );

  const renderVisualEditor = () => (
    <div>
      <Alert
        message={t('templates.editor.visual.title')}
        description={t('templates.editor.visual.desc')}
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />
      
      <TemplateVisualEditor
        templateContent={templateContent}
        onTemplateChange={handleTemplateContentChange}
      />
    </div>
  );

  const renderPlainEdit = () => (
    <div>
      <Alert
        message={t('templates.editor.plainEdit.title') || 'Plain Edit'}
        description={t('templates.editor.plainEdit.desc') || '直接编辑 JSON 格式的模板内容。保存时效果与 Visual Edit 相同。'}
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />
      
      <TextArea
        value={plainEditContent}
        onChange={(e) => setPlainEditContent(e.target.value)}
        rows={20}
        style={{
          fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, "source-code-pro", monospace',
          fontSize: '13px',
        }}
        placeholder={t('templates.editor.plainEdit.placeholder') || '请输入 JSON 格式的模板内容...'}
      />
    </div>
  );

  const renderVersionHistory = () => (
    <div>
      {template ? (
        <TemplateVersionHistory templateId={template.id} />
      ) : (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Text type="secondary">{t('templates.editor.versions.saveFirst')}</Text>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      title={
        <div>
          <FileTextOutlined style={{ marginRight: 8 }} />
          {template ? t('templates.editor.modal.titleEdit') : t('templates.editor.modal.titleCreate')}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width="95%"
      style={{ top: 20, maxHeight: '90vh' }}
      bodyStyle={{ maxHeight: 'calc(90vh - 120px)', overflowY: 'auto' }}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="save"
          type="primary"
          icon={<SaveOutlined />}
          loading={loading}
          onClick={handleSave}
        >
          {t('common.save')}
        </Button>,
      ]}
    >
      <div style={{ maxHeight: 'calc(90vh - 160px)', overflowY: 'auto' }}>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: 'basic',
              label: (
                <span>
                  <InfoCircleOutlined />
                  {t('templates.editor.tabs.basic')}
                </span>
              ),
              children: renderBasicForm(),
            },
            {
              key: 'editor',
              label: (
                <span>
                  <FileTextOutlined />
                  {t('templates.editor.tabs.visual')}
                </span>
              ),
              children: renderVisualEditor(),
            },
            {
              key: 'plain',
              label: (
                <span>
                  <CodeOutlined />
                  {t('templates.editor.tabs.plain') || 'Plain Edit'}
                </span>
              ),
              children: renderPlainEdit(),
            },
            {
              key: 'versions',
              label: (
                <span>
                  <HistoryOutlined />
                  {t('templates.editor.tabs.versions')}
                </span>
              ),
              children: renderVersionHistory(),
            },
          ]}
        />
      </div>
    </Modal>
  );
};

export default TemplateEditorModal;
